import React, { useState } from "react";
import { Box, UnstyledButton } from "@mantine/core";
import { IconCalendar } from "@tabler/icons-react";
import { dateFromKey, dayFieldLabel, dayKeyOf } from "../grid/geometry";
import { MobileDatePickerSheet } from "./MobileDatePickerSheet";
import styles from "./MobileDateField.module.css";

export interface MobileDateFieldProps {
  /** "YYYY-MM-DD" ("" = none yet). */
  value: string;
  /** The picked day as "YYYY-MM-DD". */
  onChange: (day: string) => void;
  /** Accessible name of the field ("Appointment date"). */
  label: string;
  disabled?: boolean;
  title?: string;
  /** Border / height of the surrounding form's inputs. */
  className?: string;
  /** Salon "today" ("YYYY-MM-DD") for the picker's today mark. */
  todayKey?: string;
}

const NO_BUSY_DAYS = new Set<string>();
const NEVER_CLOSED = () => false;

/** Keep clicks / Escape inside the picker portal from reaching the form. */
const stopClick = (e: React.SyntheticEvent) => e.stopPropagation();
const stopEscape = (e: React.KeyboardEvent) => {
  if (e.key === "Escape") e.stopPropagation();
};

/**
 * Date field for the mobile booking forms (new appointment, rebook,
 * reschedule): shows "Thu Oct 1, 2026" and opens the calendar header's date
 * picker sheet. Any day is pickable (past included, no clamps).
 */
export const MobileDateField: React.FC<MobileDateFieldProps> = ({
  value,
  onChange,
  label,
  disabled,
  title,
  className,
  todayKey,
}) => {
  const [open, setOpen] = useState(false);
  const date = value ? dateFromKey(value) : null;
  return (
    <>
      <UnstyledButton
        className={`${styles.trigger} ${className || ""}`}
        disabled={disabled}
        title={title}
        aria-label={label}
        aria-haspopup="dialog"
        data-value={value}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <IconCalendar size={16} stroke={1.8} className={styles.icon} />
        <Box component="span" className={styles.text} data-empty={!date}>
          {date ? dayFieldLabel(date) : "Pick a date"}
        </Box>
      </UnstyledButton>
      {open && (
        <Box
          component="span"
          className={styles.portalHost}
          onClick={stopClick}
          onKeyDown={stopEscape}
        >
          <MobileDatePickerSheet
            opened
            onClose={() => setOpen(false)}
            selectedDate={date || new Date()}
            todayKey={todayKey || dayKeyOf(new Date())}
            busyDays={NO_BUSY_DAYS}
            isClosed={NEVER_CLOSED}
            onSelectDate={(d) => onChange(dayKeyOf(d))}
          />
        </Box>
      )}
    </>
  );
};
